import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import useClickOutside from '../../../Hooks/useClickOutside';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import Heading from '../../../components/common/Typography/Heading';
import { disableScroll, enableScroll } from '../../../utils/scrollUtils';
import {
  ACTION_POPUP_CONTENT_INNER_CLASS,
  CONFIRM_POPUP_CONTENT_WIDTH_CLASS,
} from '../../../components/popup/popupLayoutSpec';
import {
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminFilterDropdownButton,
  AdminFilterChevron,
  AdminFilterMenuItem,
  AdminListFilterDropdown,
  AdminPageTitle,
  ADMIN_TOOLBAR_CLASS,
  AdminSearchField,
  AdminSupportingText,
  AdminTableBody,
  AdminTableHeader,
  AdminFormSecondaryAction,
  ADMIN_TABLE_CLASS,
  ADMIN_LIST_FILTER_MENU_CLASS,
  AdminToolbarLabel,
  adminFilterDateInputClasses,
} from '../shared/adminUx';
import {
  filterChangeHistoryForAccess,
  getAllChangeHistoryEntries,
  subscribeChangeHistory,
} from '../../../data/changeHistoryStore';
import {
  formatChangedBy,
  formatModuleDisplay,
} from '../../../data/changeHistoryEntry';
import useRolePermissions from '../../../Hooks/useRolePermissions';
import { hasFullAccess, hasModuleViewAccess } from '../../../utils/rolePermissions';
import { PLATFORM_ROLE } from '../../../data/teamConfig';

const PAGE_SIZE = 10;

const tableCols =
  'minmax(100px, 0.7fr) minmax(88px, 0.6fr) minmax(120px, 0.9fr) minmax(140px, 1fr) minmax(72px, 0.45fr) minmax(150px, 1.15fr) minmax(150px, 1.15fr) minmax(140px, 1fr) minmax(130px, 0.9fr) minmax(128px, 0.85fr)';

const TABLE_MIN_WIDTH = 'min-w-[1480px]';

const parseLogTimestamp = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

const DetailField = ({ label, value }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <AdminDetailFieldValue className="py-0 break-words whitespace-pre-wrap">
      {value || '—'}
    </AdminDetailFieldValue>
  </div>
);

const ChangeHistoryDetailsPopup = ({ entry, onClose }) => {
  useEffect(() => {
    disableScroll();
    return () => enableScroll();
  }, []);

  if (!entry) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/20 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="change-history-details-title"
    >
      <div className="flex-shrink-0 flex items-center justify-end h-[60px] sm:h-[80px] pr-4 sm:pr-6 z-10">
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center text-offblack"
          aria-label="Close"
        >
          <i className="fi fi-rs-cross text-sm cursor-pointer" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto bg-white rounded-t-xl min-h-0">
        <div className="grid min-h-full place-items-center py-10 px-4">
          <div className={`${CONFIRM_POPUP_CONTENT_WIDTH_CLASS} mx-auto hidden-scrollbar lg:pb-0 relative`}>
            <div id="change-history-details-title">
              <Heading variant="h6" className="flex items-center justify-center text-center text-offblack">
                Change History Details
              </Heading>
            </div>
            <div className={`${ACTION_POPUP_CONTENT_INNER_CLASS} space-y-6`}>
              <div className="space-y-3">
                <AdminSupportingText className="mb-0 font-semibold !text-offblack">Change Details</AdminSupportingText>
                <DetailField label="Change ID" value={entry.changeId} />
                <DetailField label="Item ID" value={entry.itemId} />
                <DetailField label="Module" value={formatModuleDisplay(entry.module)} />
                <DetailField label="Changed Item" value={entry.changedItem} />
                <DetailField label="Type" value={entry.changeType} />
                <DetailField label="Changed By" value={formatChangedBy(entry.changedBy)} />
                <DetailField label="Changed On" value={entry.changedOn} />
              </div>
              <div className="space-y-3">
                <AdminSupportingText className="mb-0 font-semibold !text-offblack">Previous Values</AdminSupportingText>
                <DetailField label="Previous Values" value={entry.previousValues} />
              </div>
              <div className="space-y-3">
                <AdminSupportingText className="mb-0 font-semibold !text-offblack">New Values</AdminSupportingText>
                <DetailField label="New Values" value={entry.newValues} />
              </div>
              <div className="space-y-3">
                <AdminSupportingText className="mb-0 font-semibold !text-offblack">Change Reason</AdminSupportingText>
                <DetailField label="Change Reason" value={entry.changeReason} />
              </div>
              <div className="space-y-3">
                <AdminSupportingText className="mb-0 font-semibold !text-offblack">Activity Details</AdminSupportingText>
                <DetailField label="Activity Details" value={entry.activityDetails || 'Not Applicable'} />
              </div>
              <div className="flex justify-center pt-2">
                <AdminFormSecondaryAction type="button" onClick={onClose}>
                  Close
                </AdminFormSecondaryAction>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SettingsHistory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeRole, isFullAccess, canViewModule } = useRolePermissions();
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [filterModule, setFilterModule] = useState(() => {
    const moduleParam = searchParams.get('module');
    return moduleParam ? formatModuleDisplay(moduleParam) : null;
  });
  const [filterChangeType, setFilterChangeType] = useState(null);
  const [filterChangedBy, setFilterChangedBy] = useState(null);
  const [dateFilter, setDateFilter] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [dateOpen, setDateOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [storeVersion, setStoreVersion] = useState(0);
  const [sourceModuleRef, setSourceModuleRef] = useState(() => searchParams.get('sourceModule') || null);
  const [sourceItemRef, setSourceItemRef] = useState(() => searchParams.get('sourceItem') || null);
  const filterRef = useRef(null);
  const dateRef = useRef(null);

  useClickOutside(filterRef, () => setFilterDropdownOpen(null));
  useClickOutside(dateRef, () => setDateOpen(false));

  const loadHistory = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    window.setTimeout(() => {
      try {
        getAllChangeHistoryEntries();
        setStoreVersion((version) => version + 1);
        setLoading(false);
      } catch {
        setLoadError(true);
        setLoading(false);
      }
    }, 280);
  }, []);

  useEffect(() => {
    loadHistory();
    return subscribeChangeHistory(() => setStoreVersion((version) => version + 1));
  }, [loadHistory]);

  useEffect(() => {
    const moduleParam = searchParams.get('module');
    const queryParam = searchParams.get('q');
    const sourceModuleParam = searchParams.get('sourceModule');
    const sourceItemParam = searchParams.get('sourceItem');
    if (moduleParam) setFilterModule(formatModuleDisplay(moduleParam));
    if (queryParam != null) setSearchQuery(queryParam);
    setSourceModuleRef(sourceModuleParam || null);
    setSourceItemRef(sourceItemParam || null);
  }, [searchParams]);

  const accessContext = useMemo(() => {
    const platformRole = activeRole?.platformRole;
    const isOwnerOrCoOwner =
      platformRole === PLATFORM_ROLE.OWNER || platformRole === PLATFORM_ROLE.CO_OWNER;
    return {
      isOwnerOrCoOwner,
      hasFullAccess: isFullAccess || hasFullAccess(activeRole),
      hasModuleView: (moduleKey) =>
        typeof canViewModule === 'function'
          ? canViewModule(moduleKey)
          : hasModuleViewAccess(activeRole, moduleKey),
    };
  }, [activeRole, isFullAccess, canViewModule]);

  const historyEntries = useMemo(() => {
    void storeVersion;
    return filterChangeHistoryForAccess(getAllChangeHistoryEntries(), accessContext);
  }, [storeVersion, accessContext]);

  const sortedHistoryEntries = useMemo(() => {
    let list = [...historyEntries];
    if (sourceModuleRef) {
      list = list.filter((entry) => entry.sourceModuleRef === sourceModuleRef);
    }
    if (sourceItemRef) {
      list = list.filter((entry) => entry.sourceItemRef === String(sourceItemRef));
    }
    return list.sort((a, b) => {
      const first = parseLogTimestamp(a.changedOnIso);
      const second = parseLogTimestamp(b.changedOnIso);
      if (!first || !second) return 0;
      return second.getTime() - first.getTime();
    });
  }, [historyEntries, sourceModuleRef, sourceItemRef]);

  const moduleOptions = useMemo(() => {
    return [...new Set(sortedHistoryEntries.map((entry) => formatModuleDisplay(entry.module)))].sort(
      (a, b) => a.localeCompare(b)
    );
  }, [sortedHistoryEntries]);

  const changeTypeOptions = useMemo(() => {
    return [...new Set(sortedHistoryEntries.map((entry) => entry.changeType))].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [sortedHistoryEntries]);

  const changedByOptions = useMemo(() => {
    return [
      ...new Set(sortedHistoryEntries.map((entry) => formatChangedBy(entry.changedBy))),
    ].sort((a, b) => a.localeCompare(b));
  }, [sortedHistoryEntries]);

  const filterDateLabel = useMemo(() => {
    if (dateFilter === 'Custom' && (dateFrom || dateTo)) return 'Custom range';
    if (dateFilter === 'Custom') return 'Custom';
    return dateFilter || 'Date Range';
  }, [dateFilter, dateFrom, dateTo]);

  const matchesDateFilter = (row) => {
    if (!dateFilter) return true;
    const logDate = parseLogTimestamp(row.changedOnIso);
    if (!logDate) return true;

    const now = new Date();
    now.setHours(23, 59, 59, 999);
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);

    if (dateFilter === 'Today') return logDate >= todayStart && logDate <= now;

    if (dateFilter === 'Last 7 days') {
      const sevenDays = new Date(todayStart);
      sevenDays.setDate(sevenDays.getDate() - 7);
      return logDate >= sevenDays && logDate <= now;
    }

    if (dateFilter === 'Last 30 days') {
      const thirtyDays = new Date(todayStart);
      thirtyDays.setDate(thirtyDays.getDate() - 30);
      return logDate >= thirtyDays && logDate <= now;
    }

    if (dateFilter === 'Custom' && (dateFrom || dateTo)) {
      if (dateFrom) {
        const from = new Date(dateFrom);
        from.setHours(0, 0, 0, 0);
        if (logDate < from) return false;
      }
      if (dateTo) {
        const to = new Date(dateTo);
        to.setHours(23, 59, 59, 999);
        if (logDate > to) return false;
      }
    }

    return true;
  };

  const filteredEntries = useMemo(() => {
    let list = [...sortedHistoryEntries];
    const query = (searchQuery || '').toLowerCase().trim();

    if (query) {
      list = list.filter(
        (entry) =>
          entry.changeId.toLowerCase().includes(query) ||
          formatModuleDisplay(entry.module).toLowerCase().includes(query) ||
          entry.module.toLowerCase().includes(query) ||
          entry.changedItem.toLowerCase().includes(query) ||
          entry.itemId.toLowerCase().includes(query) ||
          formatChangedBy(entry.changedBy).toLowerCase().includes(query) ||
          entry.changedBy.toLowerCase().includes(query)
      );
    }

    if (filterModule) {
      list = list.filter((entry) => formatModuleDisplay(entry.module) === filterModule);
    }
    if (filterChangeType) list = list.filter((entry) => entry.changeType === filterChangeType);
    if (filterChangedBy) {
      list = list.filter((entry) => formatChangedBy(entry.changedBy) === filterChangedBy);
    }

    return list.filter(matchesDateFilter);
  }, [
    searchQuery,
    filterModule,
    filterChangeType,
    filterChangedBy,
    dateFilter,
    dateFrom,
    dateTo,
    sortedHistoryEntries,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredEntries.length / PAGE_SIZE));
  const paginatedEntries = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredEntries.slice(start, start + PAGE_SIZE);
  }, [filteredEntries, currentPage]);

  const hasActiveFilters =
    Boolean((searchQuery || '').trim()) ||
    Boolean(filterModule) ||
    Boolean(filterChangeType) ||
    Boolean(filterChangedBy) ||
    Boolean(dateFilter) ||
    Boolean(sourceModuleRef) ||
    Boolean(sourceItemRef);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filterModule, filterChangeType, filterChangedBy, dateFilter, dateFrom, dateTo, sourceModuleRef, sourceItemRef]);

  const rangeStart = filteredEntries.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * PAGE_SIZE, filteredEntries.length);
  const showPagination = filteredEntries.length > PAGE_SIZE;

  const emptyMessage =
    hasActiveFilters
      ? 'No Change History records match the current search and filters.'
      : 'No Change History records are available.';

  const resetFilters = () => {
    setSearchQuery('');
    setFilterModule(null);
    setFilterChangeType(null);
    setFilterChangedBy(null);
    setDateFilter(null);
    setDateFrom('');
    setDateTo('');
    setDateOpen(false);
    setFilterDropdownOpen(null);
    setSourceModuleRef(null);
    setSourceItemRef(null);
    setCurrentPage(1);
    setSearchParams({});
  };

  const retryLoading = () => {
    loadHistory();
  };

  return (
    <AdminLayout>
      <div className="w-full min-w-0 max-w-full">
        <AdminPageTitle>Change History</AdminPageTitle>

        <DashboardMainContainer className={`${ADMIN_TABLE_CLASS} max-w-full min-w-0`}>
          <div className={ADMIN_TOOLBAR_CLASS}>
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between min-w-0">
              <div className="w-full max-w-[450px] min-w-0">
                <AdminSearchField
                  type="text"
                  placeholder="Search by Change ID, Module, Changed Item, Item ID, Changed By"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  aria-label="Search Change History"
                />
              </div>

              <div
                className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end min-w-0"
                ref={filterRef}
              >
                <div className="relative" ref={dateRef}>
                  <AdminFilterDropdownButton
                    type="button"
                    onClick={() => {
                      setDateOpen(!dateOpen);
                      setFilterDropdownOpen(null);
                    }}
                    aria-expanded={dateOpen}
                    aria-label="Date Range"
                  >
                    <span className="truncate">{filterDateLabel}</span>
                    <AdminFilterChevron open={dateOpen} />
                  </AdminFilterDropdownButton>
                  {dateOpen && (
                    <div className={`${ADMIN_LIST_FILTER_MENU_CLASS} !max-w-[18rem]`}>
                      {[null, 'Today', 'Last 7 days', 'Last 30 days', 'Custom'].map((option) => (
                        <AdminFilterMenuItem
                          key={option || 'all'}
                          active={dateFilter === option}
                          onClick={() => {
                            if (option !== 'Custom') {
                              setDateFilter(option);
                              setDateFrom('');
                              setDateTo('');
                              setDateOpen(false);
                            } else {
                              setDateFilter('Custom');
                            }
                          }}
                        >
                          {option || 'All'}
                        </AdminFilterMenuItem>
                      ))}
                      {dateFilter === 'Custom' && (
                        <div className="grid grid-cols-2 gap-2 mt-2 pt-2 px-3 border-t border-lightgray">
                          <div>
                            <AdminToolbarLabel className="block mb-1">From</AdminToolbarLabel>
                            <input
                              type="date"
                              value={dateFrom}
                              onChange={(event) => setDateFrom(event.target.value)}
                              className={adminFilterDateInputClasses()}
                            />
                          </div>
                          <div>
                            <AdminToolbarLabel className="block mb-1">To</AdminToolbarLabel>
                            <input
                              type="date"
                              value={dateTo}
                              onChange={(event) => setDateTo(event.target.value)}
                              className={adminFilterDateInputClasses()}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <AdminListFilterDropdown
                  label="Module"
                  value={filterModule}
                  options={[
                    { value: null, label: 'All' },
                    ...moduleOptions.map((module) => ({ value: module, label: module })),
                  ]}
                  onChange={setFilterModule}
                  open={filterDropdownOpen === 'module'}
                  setOpen={(next) => {
                    setDateOpen(false);
                    setFilterDropdownOpen(next ? 'module' : null);
                  }}
                />

                <AdminListFilterDropdown
                  label="Type"
                  value={filterChangeType}
                  options={[
                    { value: null, label: 'All' },
                    ...changeTypeOptions.map((type) => ({ value: type, label: type })),
                  ]}
                  onChange={setFilterChangeType}
                  open={filterDropdownOpen === 'changeType'}
                  setOpen={(next) => {
                    setDateOpen(false);
                    setFilterDropdownOpen(next ? 'changeType' : null);
                  }}
                />

                <AdminListFilterDropdown
                  label="Changed By"
                  value={filterChangedBy}
                  options={[
                    { value: null, label: 'All' },
                    ...changedByOptions.map((name) => ({ value: name, label: name })),
                  ]}
                  onChange={setFilterChangedBy}
                  open={filterDropdownOpen === 'changedBy'}
                  setOpen={(next) => {
                    setDateOpen(false);
                    setFilterDropdownOpen(next ? 'changedBy' : null);
                  }}
                />

                {hasActiveFilters ? (
                  <AdminFormSecondaryAction type="button" onClick={resetFilters}>
                    Reset Filters
                  </AdminFormSecondaryAction>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-6 w-full min-w-0 overflow-x-auto">
            <div className={`w-full ${TABLE_MIN_WIDTH}`}>
              <div
                className="grid w-full gap-x-3 gap-y-2 py-3 px-4 border-b border-lightgray text-offblack sticky top-0 bg-white z-10"
                style={{ gridTemplateColumns: tableCols }}
              >
                {[
                  'Change ID',
                  'Item ID',
                  'Module',
                  'Changed Item',
                  'Type',
                  'Previous Values',
                  'New Values',
                  'Change Reason',
                  'Changed By',
                  'Changed On',
                ].map((header) => (
                  <div key={header} className="flex items-center min-w-0">
                    <AdminTableHeader className="break-words">{header}</AdminTableHeader>
                  </div>
                ))}
              </div>

              {loading ? (
                <div className="py-8 text-center text-mediumgray px-4">
                  <AdminSupportingText>Loading…</AdminSupportingText>
                </div>
              ) : loadError ? (
                <div className="py-8 text-center space-y-3 px-4">
                  <AdminSupportingText className="text-mediumgray">
                    Change History could not be loaded. Try again.
                  </AdminSupportingText>
                  <AdminFormSecondaryAction type="button" onClick={retryLoading}>
                    Retry Loading
                  </AdminFormSecondaryAction>
                </div>
              ) : paginatedEntries.length > 0 ? (
                paginatedEntries.map((entry) => (
                  <div
                    key={entry.changeId}
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedEntry(entry)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedEntry(entry);
                      }
                    }}
                    className="grid w-full gap-x-3 gap-y-2 py-3 px-4 border-b border-lightgray text-offblack items-start cursor-pointer rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-primaryblue focus-visible:ring-offset-1"
                    style={{ gridTemplateColumns: tableCols }}
                  >
                    {[
                      entry.changeId,
                      entry.itemId,
                      formatModuleDisplay(entry.module),
                      entry.changedItem,
                      entry.changeType,
                      entry.previousValues,
                      entry.newValues,
                      entry.changeReason,
                      formatChangedBy(entry.changedBy),
                      entry.changedOn,
                    ].map((value, index) => (
                      <div
                        key={`${entry.changeId}-${index}`}
                        className="flex items-start min-w-0"
                        title={typeof value === 'string' ? value : undefined}
                      >
                        <AdminTableBody className="break-words block whitespace-pre-wrap">
                          {value}
                        </AdminTableBody>
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <div className="py-8 text-center text-mediumgray px-4">
                  <AdminSupportingText>{emptyMessage}</AdminSupportingText>
                  {hasActiveFilters ? (
                    <div className="mt-3">
                      <AdminFormSecondaryAction type="button" onClick={resetFilters}>
                        Reset Filters
                      </AdminFormSecondaryAction>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          </div>

          <ConfigurationListPaginationFooter
            show={!loading && !loadError && filteredEntries.length > 0 && showPagination}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            totalCount={filteredEntries.length}
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            goToPage={setCurrentPage}
          />
        </DashboardMainContainer>

        {selectedEntry ? (
          <ChangeHistoryDetailsPopup
            entry={selectedEntry}
            onClose={() => setSelectedEntry(null)}
          />
        ) : null}
      </div>
    </AdminLayout>
  );
};

export default SettingsHistory;
